import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Header.css';
import ActiveLink from '../ActiveLink';
import {
  HiMenu,
  HiOutlineUserCircle,
  HiOutlineLogout,
  HiOutlineUser,
  HiChevronDown,
} from 'react-icons/hi';
import { Coins, Bookmark } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import bimartIsoImg from '../../assets/bimart-iso19650.png';
import { NAV_BUTTON_BASE } from './navButtonStyles';

const NavDropdown = ({ label, children }) => (
  <li className="relative group">
    <button className={`${NAV_BUTTON_BASE} group-hover:text-indigo-600 group-hover:bg-indigo-50`}>
      {label}
      <HiChevronDown className="w-4 h-4 group-hover:rotate-180 transition-transform" />
    </button>
    <div className="absolute top-full left-0 bg-white shadow-xl rounded-xl border border-gray-100 py-2 w-56 scale-y-0 group-hover:scale-y-100 origin-top transition-transform duration-200 z-50">
      {children}
    </div>
  </li>
);

const NavDropdownItem = ({ to, title, desc, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    className="block px-4 py-2.5 hover:bg-indigo-50 transition"
  >
    <span className="font-semibold text-gray-800 text-sm">{title}</span>
    {desc && <p className="text-xs text-gray-500 mt-0.5">{desc}</p>}
  </Link>
);

const Header = React.memo(() => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [prevScrollPos, setPrevScrollPos] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  const {
    user,
    isAuthenticated,
    isAdmin,
    credits,
    logout: authLogout,
    refreshCredits,
  } = useAuth();

  const totalCredits = useMemo(
    () => (credits?.available || 0) + (credits?.extra || 0),
    [credits],
  );

  const toggleMenu = useCallback(() => setIsMenuOpen((prev) => !prev), []);
  const handleLogout = useCallback(() => {
    authLogout();
    navigate('/login');
  }, [authLogout, navigate]);

  useEffect(() => {
    const handleCreditsUpdate = () => refreshCredits();
    window.addEventListener('creditsUpdated', handleCreditsUpdate);
    return () =>
      window.removeEventListener('creditsUpdated', handleCreditsUpdate);
  }, [refreshCredits]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.scrollY;
      setIsScrolled(currentScrollPos > prevScrollPos && currentScrollPos > 250);
      setPrevScrollPos(currentScrollPos);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [prevScrollPos]);

  return (
    <>
      <header
        className={`bg-gradient-to-br from-indigo-50 via-white to-blue-100 shadow-lg border-b border-slate-50 sticky top-0 w-full z-50 transition py-2 duration-300 ease-in-out ${
          isScrolled ? '-translate-y-full' : 'translate-y-0'
        }`}
      >
        <div className="container max-w-7xl mx-auto px-4">
          <nav className="flex justify-between items-center">
            {/* Logo */}
            <figure>
              <Link
                to="/"
                className="text-xl md:text-2xl font-extrabold tracking-tight text-indigo-700 hover:text-indigo-800 transition drop-shadow-sm whitespace-nowrap"
              >
                Licitatii-Constructii.ro
              </Link>
            </figure>

            {/* Desktop Menu */}
            <ul className="hidden lg:flex items-center gap-1">
              <ActiveLink to="/">Acasa</ActiveLink>

              <NavDropdown label="Proceduri">
                <NavDropdownItem
                  to="/licitatii"
                  title="Licitatii"
                  desc="Anunturi de participare CN, SCN, RFQ, PC, DC, RFD"
                />
                <NavDropdownItem
                  to="/achizitii-directe"
                  title="Achizitii Directe"
                  desc="Cumparari directe publicate in SEAP"
                />
                <NavDropdownItem
                  to="/anunturi-intentie"
                  title="Anunturi de Intentie"
                  desc="Planificari viitoare de achizitii publice"
                />
                <NavDropdownItem
                  to="/anunturi-atribuire"
                  title="Anunturi Atribuire"
                  desc="Rezultate si contracte atribuite"
                />
              </NavDropdown>

              <ActiveLink to="/proiecte">Proiecte</ActiveLink>
              <ActiveLink to="/abonamente">Abonamente</ActiveLink>

              <NavDropdown label="Resurse">
                <NavDropdownItem
                  to="/wiki"
                  title="Wiki"
                  desc="Ghiduri si documentatie pentru achizitii publice"
                />
                <NavDropdownItem
                  to="/contact"
                  title="Contact"
                  desc="Contacteaza echipa Licitatii-Constructii"
                />
              </NavDropdown>
            </ul>

            {/* Auth section */}
            <div className="flex items-center gap-4">
              <button
                className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-md bg-white border border-indigo-200 text-indigo-600 shadow-sm hover:bg-indigo-50 hover:border-indigo-400 transition"
                onClick={toggleMenu}
                aria-label="Meniu"
              >
                <HiMenu className="w-6 h-6" />
              </button>

              {isAuthenticated ? (
                <div className="flex items-center gap-3">
                  {/* Credits display */}
                  <div className="hidden lg:flex items-center bg-gradient-to-r from-blue-50 to-indigo-50 border border-indigo-200 rounded-xl px-4 py-2 shadow-sm">
                    <div className="flex items-center mr-3">
                      <div className="flex items-center justify-center w-8 h-8 bg-indigo-100 rounded-full mr-2">
                        <Coins className="w-4 h-4 text-indigo-600" />
                      </div>
                      <div className="flex flex-col">
                        <span className="font-bold text-lg text-indigo-800">
                          {totalCredits}
                        </span>
                        <span className="text-xs text-indigo-600 font-medium -mt-1">
                          credite totale
                        </span>
                      </div>
                    </div>
                    <div className="text-xs text-gray-600">
                      <div className="flex items-center gap-1 mb-0.5">
                        <span className="w-2 h-2 bg-indigo-400 rounded-full"></span>
                        <span>{credits?.available || 0} din abonament</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="w-2 h-2 bg-emerald-400 rounded-full"></span>
                        <span>{credits?.extra || 0} extra cumparate</span>
                      </div>
                    </div>
                  </div>

                  {/* User avatar + dropdown */}
                  <div className="relative group">
                    <button className="w-10 h-10 bg-indigo-500 text-white text-sm font-bold flex items-center justify-center rounded-full shadow-lg">
                      {user?.name?.slice(0, 2)?.toUpperCase() || (
                        <HiOutlineUser className="w-5 h-5" />
                      )}
                    </button>
                    <div className="absolute top-full right-0 bg-white shadow-xl rounded-xl border border-gray-100 p-2 w-56 scale-y-0 group-hover:scale-y-100 origin-top transition-transform duration-200 z-50">
                      <div className="px-3 py-2 border-b mb-1">
                        <p className="font-semibold text-indigo-700 truncate">
                          {user?.name}
                        </p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                          <span>{credits?.available || 0} din abonament</span>
                          <span>{credits?.extra || 0} extra</span>
                        </div>
                      </div>
                      <button
                        onClick={() => navigate('/account')}
                        className="w-full text-left px-3 py-2 text-gray-700 hover:bg-indigo-50 rounded-lg transition flex items-center gap-2 text-sm"
                      >
                        <HiOutlineUserCircle className="w-4 h-4" /> Contul Meu
                      </button>
                      <Link
                        to="/proceduri-salvate"
                        className="w-full text-left px-3 py-2 text-gray-700 hover:bg-indigo-50 rounded-lg transition flex items-center gap-2 text-sm"
                      >
                        <Bookmark className="w-4 h-4" /> Procedurile Mele
                      </Link>
                      <Link
                        to="/abonamente"
                        className="w-full text-left px-3 py-2 text-gray-700 hover:bg-indigo-50 rounded-lg transition flex items-center gap-2 text-sm"
                      >
                        <Coins className="w-4 h-4" /> Abonamente & Credite
                      </Link>
                      {isAdmin && (
                        <Link
                          to="/admin"
                          className="w-full text-left px-3 py-2 text-indigo-700 font-semibold hover:bg-indigo-50 rounded-lg transition flex items-center gap-2 text-sm border-t mt-1 pt-2"
                        >
                          📊 Admin dashboard
                        </Link>
                      )}
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition flex items-center gap-2 text-sm mt-1"
                      >
                        <HiOutlineLogout className="w-4 h-4" /> Logout
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  to="/login"
                  className="text-white bg-gradient-to-r from-indigo-500 to-green-500 hover:from-indigo-600 hover:to-green-600 font-bold rounded-xl text-sm px-5 py-2 shadow-lg transition-all"
                >
                  Login
                </Link>
              )}
            </div>
          </nav>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="fixed inset-0 z-[9999] h-screen flex items-start justify-end lg:hidden">
              <div
                className="absolute inset-0 bg-black bg-opacity-40 backdrop-blur-sm"
                onClick={toggleMenu}
              />
              <div className="absolute top-0 right-0 bg-white w-5/6 max-w-sm h-screen p-5 flex flex-col gap-2 z-[10000] shadow-2xl overflow-y-auto">
                <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                  <Link
                    to="/"
                    onClick={toggleMenu}
                    className="text-base font-extrabold tracking-tight text-indigo-700"
                  >
                    Licitatii-Constructii.ro
                  </Link>
                  <button
                    className="text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-full w-8 h-8 flex items-center justify-center text-xl font-bold transition"
                    onClick={toggleMenu}
                    aria-label="Închide meniul"
                  >
                    ×
                  </button>
                </div>

                <ul className="flex flex-col gap-1.5 mt-2">
                  {[
                    { to: '/', label: 'Acasa' },
                    { to: '/licitatii', label: 'Licitatii' },
                    { to: '/achizitii-directe', label: 'Achizitii Directe' },
                    { to: '/anunturi-intentie', label: 'Anunturi de Intentie' },
                    {
                      to: '/anunturi-atribuire',
                      label: 'Anunturi Atribuire',
                    },
                    { to: '/proiecte', label: 'Proiecte' },
                    { to: '/abonamente', label: 'Abonamente' },
                    { to: '/wiki', label: 'Wiki' },
                    { to: '/contact', label: 'Contact' },
                    ...(isAuthenticated
                      ? [{ to: '/proceduri-salvate', label: 'Procedurile Mele' }]
                      : []),
                  ].map((item) => (
                    <li key={item.to}>
                      <Link
                        to={item.to}
                        className="block px-3 py-2.5 text-sm font-semibold text-indigo-700 bg-indigo-50 border border-indigo-100 hover:bg-indigo-100 hover:border-indigo-300 rounded-md transition"
                        onClick={toggleMenu}
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>

                <div className="mt-4 border-t pt-3">
                  {isAuthenticated ? (
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2 px-3 py-2 bg-indigo-50 rounded-lg">
                        <span className="w-8 h-8 flex items-center justify-center rounded-full bg-indigo-500 text-white font-bold text-sm">
                          {user?.name?.slice(0, 2)?.toUpperCase()}
                        </span>
                        <div className="flex-1">
                          <span className="font-semibold text-sm text-indigo-800 block truncate">
                            {user?.name}
                          </span>
                          <span className="text-xs text-indigo-600">
                            {totalCredits} credite
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          navigate('/account');
                          toggleMenu();
                        }}
                        className="px-4 py-2 text-sm font-medium hover:bg-indigo-50 rounded-lg flex items-center gap-2"
                      >
                        <HiOutlineUserCircle className="w-4 h-4" /> Contul Meu
                      </button>
                      <button
                        onClick={() => {
                          handleLogout();
                          toggleMenu();
                        }}
                        className="px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-2"
                      >
                        <HiOutlineLogout className="w-4 h-4" /> Logout
                      </button>
                    </div>
                  ) : (
                    <Link
                      to="/login"
                      className="block text-center text-white bg-gradient-to-r from-indigo-500 to-green-500 font-bold rounded-xl py-2.5 shadow-lg"
                      onClick={toggleMenu}
                    >
                      Login
                    </Link>
                  )}
                </div>

                <p className="mt-4 pt-3 border-t border-gray-100 text-[11px] text-gray-400 text-center">
                  &copy; 2026 Licitatii-Constructii.ro
                </p>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Banner */}
      <div className="w-full bg-gradient-to-r from-blue-900 via-indigo-900 to-purple-900 py-2 px-4 flex items-center justify-center text-white text-sm font-semibold tracking-wide shadow-md mb-2">
        <img
          src={bimartIsoImg}
          alt="Certificare BIM ISO 19650"
          className="h-7 w-7 mr-2 rounded bg-white p-1 border border-blue-300"
          style={{ background: '#0a0a23' }}
        />
        <span className="mr-2">
          NOU: Certificare ISO 19650 pentru BIM cu BIMART Solutions
        </span>
        <a
          href="https://certificarebim.ro/"
          target="_blank"
          rel="noopener noreferrer"
          className="underline text-pink-300 hover:text-pink-400 font-bold ml-2"
        >
          Afla mai multe &rarr;
        </a>
      </div>
    </>
  );
});

export default Header;
